import { useCallback } from 'react'
import type { MapLayerMouseEvent } from 'maplibre-gl'
import { useAppStore } from '~/hooks'
import { MapTooltipMachineContext } from '~/contexts/MapTooltipMachineContext'
import { queryMapTooltipAtPoint, type MapTooltipQueryResult } from '~/util/mapTooltipQuery'
import { track } from '~/analytics'

function inspectedFeature(
  hit: MapTooltipQueryResult
): 'wave_point' | 'adaptation' | 'flood_depth' | null {
  if (hit.adaptationType) return 'adaptation'
  if (hit.wavePointName) return 'wave_point'
  if (hit.floodDepthLabel) return 'flood_depth'
  return null
}

/**
 * Custom hook to handle map click events.
 * @returns An object containing the onClick handler.
 */
function useMapClick() {
  const { mapRef, deckRef, DEM, returnPeriod, adaptation, contourFloodEnabled } = useAppStore()
  const { send } = MapTooltipMachineContext.useActorRef()

  const onClick = useCallback(
    (event: MapLayerMouseEvent) => {
      const hit = queryMapTooltipAtPoint({
        mapRef,
        deckRef,
        point: event.point,
        DEM,
        returnPeriod,
        adaptation,
        contourFloodEnabled,
      })

      const feature = inspectedFeature(hit)
      if (feature) {
        track('map_feature_inspected', { feature })
      }

      send({
        type: 'MAP_CLICKED',
        x: event.point.x,
        y: event.point.y,
        lat: event.lngLat.lat,
        lng: event.lngLat.lng,
        ...hit,
      })
    },
    [mapRef, deckRef, DEM, returnPeriod, adaptation, contourFloodEnabled, send]
  )

  return { onClick }
}

export default useMapClick
